/**
 * standings-renderer.ts - Standings Rendering Functions
 *
 * Handles rendering of standings tables, including:
 * - Swiss/Round-Robin standings tables
 * - Initial rankings
 * - Round selectors
 */

import type { Tournament, Player, Standing, Category, TiebreakCriterion } from '../../../types/api';
import { TIEBREAK_DEFINITIONS, DEFAULT_TIEBREAK_CRITERIA } from '../../../types/api';
import { TOURNAMENT_FORMATS } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';
import { escapeHtml } from '../../utils/security';
import { formatPlayerDisplay } from '../../utils/formatting';
import { renderKnockoutBracket } from './knockout-bracket';
import { getPlayerCategories } from '../categories';
import { renderCrosstable } from './crosstable-renderer';

// Tiebreak criteria that cannot be displayed as numeric columns (comparison-only)
const NON_DISPLAYABLE_TIEBREAKS: TiebreakCriterion[] = ['directEncounter', 'extendedDE'];

interface TiebreakColumn {
  key: TiebreakCriterion;
  header: string;
  legendDesc: string;
}

/**
 * Get tiebreak column configuration based on tournament settings
 */
function getTiebreakColumns(tiebreakCriteria?: TiebreakCriterion[]): TiebreakColumn[] {
  const criteria = tiebreakCriteria || DEFAULT_TIEBREAK_CRITERIA;

  // Filter out non-displayable criteria and map to column config
  return criteria
    .filter(c => !NON_DISPLAYABLE_TIEBREAKS.includes(c))
    .map(key => {
      const def = TIEBREAK_DEFINITIONS[key];
      if (!def) {
        return null;
      }
      return {
        key,
        header: def.abbrev,
        legendDesc: t(def.nameKey, def.abbrev)
      };
    })
    .filter((col): col is TiebreakColumn => col !== null);
}

/**
 * Get tiebreak cell value for a standing
 */
function getTiebreakValue(standing: Standing, key: TiebreakCriterion): string {
  // First check dynamic tiebreakScores (from TieBreakServer)
  let value: number | undefined = standing.tiebreakScores?.[key];

  // Fall back to legacy fields for backwards compatibility
  if (value === undefined) {
    switch (key) {
      case 'buchholz':
        value = standing.buchholz;
        break;
      case 'buchholzSum':
      case 'buchholzMedian':
        value = standing.buchholzSum;
        break;
      case 'sonnebornBerger':
        value = standing.sonnebornBerger;
        break;
      case 'progressiveScore':
        value = standing.progressiveScore;
        break;
      case 'koya':
        value = standing.koya;
        break;
      case 'averageRatingOpp':
        value = standing.averageRatingOpp;
        break;
      case 'performanceRating':
        value = standing.performanceRating;
        break;
      case 'wins':
        value = standing.wins;
        break;
    }
  }

  if (value === undefined || value === null) {
    return '-';
  }

  // Format based on tiebreak type (some are integers, some are decimals)
  const integerTiebreaks: TiebreakCriterion[] = ['wins', 'gamesWithBlack', 'winsWithBlack', 'rating', 'startNumber', 'initialRank'];
  if (integerTiebreaks.includes(key)) {
    return Math.round(value).toString();
  }

  return value.toFixed(1);
}

/**
 * Render category filter as horizontal pills
 * @param categories - Array of categories
 * @param tournamentId - The tournament ID
 * @param selectedCategoryId - Currently selected category ID
 * @returns HTML string for category pills
 */
function renderCategoryFilter(categories: Category[], tournamentId: string, selectedCategoryId: string | null): string {
  if (!categories || categories.length === 0) {
    return '';
  }

  const pills = categories.map(cat => `
    <button type="button" class="standings-category-pill ${selectedCategoryId === cat.id ? 'active' : ''}"
      data-category-id="${cat.id}"
      onclick="filterStandingsByCategory('${tournamentId}', '${selectedCategoryId === cat.id ? '' : cat.id}')">
      ${escapeHtml(cat.name)}
    </button>
  `).join('');

  return `
    <div class="standings-category-filter">
      ${pills}
    </div>
  `;
}

// Track current view mode per tournament
const standingsViewMode = new Map<string, 'standings' | 'crosstable'>();

/**
 * Get current view mode for a tournament
 */
export function getStandingsViewMode(tournamentId: string): 'standings' | 'crosstable' {
  return standingsViewMode.get(tournamentId) || 'standings';
}

/**
 * Set view mode for a tournament
 */
export function setStandingsViewMode(tournamentId: string, mode: 'standings' | 'crosstable'): void {
  standingsViewMode.set(tournamentId, mode);
}

/**
 * Switch standings view mode and reload
 */
export async function switchStandingsView(tournamentId: string, mode: 'standings' | 'crosstable'): Promise<void> {
  setStandingsViewMode(tournamentId, mode);
  // Reload standings with the new view mode
  if (window.loadStandings) {
    await window.loadStandings(tournamentId);
  }
}

/**
 * Render standings table
 * @param standings - Array of standing objects
 * @param tournament - Tournament object
 * @param tournamentId - The tournament ID
 * @param viewingRound - The round being viewed
 * @param categories - Array of categories for filtering
 * @param selectedCategoryId - Currently selected category ID
 * @returns HTML string for the standings
 */
export function renderStandings(
  standings: Standing[],
  tournament: Tournament,
  tournamentId: string,
  viewingRound: number | null = null,
  categories: Category[] = [],
  selectedCategoryId: string | null = null
): string {
  const viewMode = getStandingsViewMode(tournamentId);
  // For knockout tournaments, show bracket instead of standings table
  if (tournament.format === TOURNAMENT_FORMATS.KNOCKOUT) {
    return renderKnockoutBracket(tournament, tournamentId);
  }

  // Handle case when standings is empty but category filter is selected (show filter controls)
  const hasFilteredEmptyResults = (!standings || standings.length === 0) && selectedCategoryId;
  const currentRound = tournament.currentRound || 0;

  // For Swiss tournaments before round 1, show initial ranking instead of empty standings
  const isPreRound1Swiss = tournament.format === TOURNAMENT_FORMATS.SWISS && currentRound < 1;

  if (!standings || standings.length === 0 || isPreRound1Swiss) {
    // If category filter is active with no results, continue to show the filter UI
    if (!hasFilteredEmptyResults) {
      // If there are players but no standings, show initial ranking using existing function
      if (tournament.players && tournament.players.length > 0) {
        const matches = tournament.matches || [];

        // Include all players with joinedRound info, sort original players first then late joiners
        const playersWithJoinedRound = tournament.players.map((player: Player) => {
          const playerMatches = matches.filter(m =>
            m.player1?.id === player.id || m.player2?.id === player.id
          );
          const joinedRound = playerMatches.length > 0
            ? Math.min(...playerMatches.map(m => m.round))
            : undefined;
          return { ...player, joinedRound };
        });

        // Sort: original players first (by initialRanking/rating), then late joiners at the end
        const sortedPlayers = playersWithJoinedRound.sort((a, b) => {
          // Separate original players from late joiners
          const aIsLateJoiner = a.joinedRound !== undefined && a.joinedRound > 1;
          const bIsLateJoiner = b.joinedRound !== undefined && b.joinedRound > 1;

          if (aIsLateJoiner !== bIsLateJoiner) {
            return aIsLateJoiner ? 1 : -1;
          }

          // Within each group, sort by initialRanking or rating
          if (a.initialRanking !== undefined && b.initialRanking !== undefined) {
            return a.initialRanking - b.initialRanking;
          }
          if (a.initialRanking !== undefined) return -1;
          if (b.initialRanking !== undefined) return 1;
          return (b.rating || 0) - (a.rating || 0);
        });
        return renderRankingInStandings(sortedPlayers, tournament, tournamentId);
      }
      return `<p>${t('standings.noStandings', 'No standings available yet. Add players and generate pairings to see standings.')}</p>`;
    }
  }

  const activeRound = viewingRound || currentRound;
  const isRoundRobin = tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN;

  // Create round selector buttons
  let roundSelector = '';
  if (currentRound >= 1) {
    const roundButtons: string[] = [];

    // Only show Initial Ranking button for Swiss tournaments
    if (tournament.format === TOURNAMENT_FORMATS.SWISS) {
      roundButtons.push(`<button type="button" class="btn btn-small standings-round-btn btn-secondary" data-tournament="${tournamentId}" data-round="0" data-type="ranking">${t('standings.initialRanking', 'Initial Ranking')}</button>`);
    }

    for (let i = 1; i <= currentRound; i++) {
      const isActiveRound = (i === activeRound && viewMode !== 'crosstable');
      roundButtons.push(`<button type="button" class="btn btn-small standings-round-btn ${isActiveRound ? 'btn-primary' : 'btn-secondary'}" data-tournament="${tournamentId}" data-round="${i}">${t('round.round', 'Round')} ${i}</button>`);
    }

    // Add Crosstable button after last round for round robin tournaments
    if (isRoundRobin) {
      const isCrosstableActive = viewMode === 'crosstable';
      roundButtons.push(`<button type="button" class="btn btn-small standings-round-btn ${isCrosstableActive ? 'btn-primary' : 'btn-secondary'}" data-tournament="${tournamentId}" data-type="crosstable" onclick="switchStandingsView('${tournamentId}', 'crosstable')">${t('standings.crosstable', 'Crosstable')}</button>`);
    }

    roundSelector = `
      <div class="round-selector standings-round-selector">
        <div class="round-selector-buttons">
          ${roundButtons.join('')}
        </div>
        <div class="round-selector-actions">
          ${renderCategoryFilter(categories, tournamentId, selectedCategoryId)}
          <button class="btn-icon" onclick="printStandings('${tournamentId}')" title="${t('standings.printStandings', 'Print standings')}">🖨</button>
        </div>
      </div>
    `;
  }

  // If crosstable view is selected for round robin, render that instead
  if (isRoundRobin && viewMode === 'crosstable' && currentRound >= 1) {
    return `
      <div class="standings-table-container">
        ${roundSelector}
        ${renderCrosstable(tournament, standings, tournamentId)}
      </div>
    `;
  }

  // Category filter for when there are no rounds yet
  const categoryFilterStandalone = currentRound < 1 && categories.length > 0
    ? `<div class="round-selector standings-round-selector">
        <div class="round-selector-actions" style="justify-content: flex-end;">
          ${renderCategoryFilter(categories, tournamentId, selectedCategoryId)}
        </div>
      </div>`
    : '';

  // Get tiebreak columns based on tournament configuration
  const tiebreakColumns = getTiebreakColumns(tournament.tiebreakCriteria);

  // Build header row with dynamic tiebreak columns
  const tiebreakHeaders = tiebreakColumns.map(col => `<th>${col.header}</th>`).join('');

  let html = `
    <div class="standings-table-container">
      ${roundSelector || categoryFilterStandalone}
      <table class="standings-table">
        <thead>
          <tr>
            <th>#</th>
            <th>${t('standings.player', 'Player')}</th>
            <th>${t('standings.points', 'Points')}</th>
            <th>${t('standings.wdl', 'W-D-L')}</th>
            ${tiebreakHeaders}
          </tr>
        </thead>
        <tbody>
  `;

  // Calculate reference date for category matching
  const referenceDate = tournament.startDate ? new Date(tournament.startDate) : new Date();

  // Show empty state message if filtering resulted in no matches
  if (standings.length === 0 && selectedCategoryId) {
    const colCount = 4 + tiebreakColumns.length;
    html += `
      <tr>
        <td colspan="${colCount}" style="text-align: center; color: var(--text-tertiary); padding: 20px;">
          ${t('standings.noCategoryPlayers', 'No players match this category')}
        </td>
      </tr>
    `;
  }

  standings.forEach(standing => {
    if (!standing.player) {
      console.error('Standing missing player:', standing);
      return;
    }
    const playerDisplay = formatPlayerDisplay(standing.player);

    // Get categories for this player
    const playerCategories = categories.length > 0 ? getPlayerCategories(standing.player, categories, referenceDate) : [];
    const categoryBadgesHtml = playerCategories.length > 0
      ? `<span class="player-category-badges">${playerCategories.map(cat => `<span class="player-category-badge">${escapeHtml(cat.name)}</span>`).join('')}</span>`
      : '';

    // Build tiebreak cells dynamically
    const tiebreakCells = tiebreakColumns.map(col =>
      `<td>${getTiebreakValue(standing, col.key)}</td>`
    ).join('');

    html += `
      <tr>
        <td><strong>${standing.rank}</strong></td>
        <td>${playerDisplay}${categoryBadgesHtml}</td>
        <td><strong>${standing.points}</strong></td>
        <td>${standing.wins}-${standing.draws}-${standing.losses}</td>
        ${tiebreakCells}
      </tr>
    `;
  });

  // Build legend dynamically based on displayed tiebreak columns
  const tiebreakLegendItems = tiebreakColumns.map(col =>
    `<p><small><strong>${col.header}:</strong> ${col.legendDesc}</small></p>`
  ).join('');

  html += `
        </tbody>
      </table>
      <div class="standings-legend">
        <p><small><strong>${t('standings.wdl', 'W-D-L')}:</strong> ${t('standings.wdlLegend', 'Wins-Draws-Losses')}</small></p>
        ${tiebreakLegendItems}
      </div>
    </div>
  `;

  return html;
}

/**
 * Render ranking in standings view
 * @param ranking - Array of player rankings
 * @param tournament - Tournament object
 * @param tournamentId - The tournament ID
 * @returns HTML string for the ranking
 */
export function renderRankingInStandings(ranking: Player[], tournament: Tournament, tournamentId: string): string {
  if (!ranking || ranking.length === 0) {
    return `<p>${t('standings.noRanking', 'No ranking available yet.')}</p>`;
  }

  const currentRound = tournament.currentRound || 0;

  // Create round selector buttons
  let roundSelector = '';
  const roundButtons: string[] = [];

  // Only show Initial Ranking button for Swiss tournaments
  if (tournament.format === TOURNAMENT_FORMATS.SWISS) {
    // Before round 1, show disabled button; after round 1, show active button
    const isPreRound1 = currentRound < 1;
    roundButtons.push(`<button type="button" class="btn btn-small standings-round-btn btn-primary" data-tournament="${tournamentId}" data-round="0" data-type="ranking"${isPreRound1 ? ' disabled' : ''}>${t('standings.initialRanking', 'Initial Ranking')}</button>`);
  }

  // Add all round buttons
  for (let i = 1; i <= currentRound; i++) {
    roundButtons.push(`<button type="button" class="btn btn-small standings-round-btn btn-secondary" data-tournament="${tournamentId}" data-round="${i}">${t('round.round', 'Round')} ${i}</button>`);
  }

  roundSelector = `
    <div class="round-selector standings-round-selector">
      <div class="round-selector-buttons">
        ${roundButtons.join('')}
      </div>
      <div class="round-selector-actions">
        <button class="btn-icon" onclick="printStandings('${tournamentId}')" title="${t('standings.printStandings', 'Print standings')}">🖨</button>
      </div>
    </div>
  `;

  // Get tiebreak columns based on tournament configuration
  const tiebreakColumns = getTiebreakColumns(tournament.tiebreakCriteria);
  const tiebreakHeaders = tiebreakColumns.map(col => `<th>${col.header}</th>`).join('');
  const emptyTiebreakCells = tiebreakColumns.map(() => '<td>-</td>').join('');

  let html = `
    <div class="standings-table-container">
      ${roundSelector}
      <table class="standings-table">
        <thead>
          <tr>
            <th>#</th>
            <th>${t('standings.player', 'Player')}</th>
            <th>${t('standings.points', 'Points')}</th>
            <th>${t('standings.wdl', 'W-D-L')}</th>
            ${tiebreakHeaders}
          </tr>
        </thead>
        <tbody>
  `;

  ranking.forEach((player: Player & { joinedRound?: number }, index) => {
    const playerName = `${escapeHtml(player.firstName)} ${escapeHtml(player.lastName)}`.trim();
    const playerDisplay = player.federation ? `${playerName} (${escapeHtml(player.federation)})` : playerName;
    // Show tag for late joiners (players who joined after round 1)
    const lateJoinerTag = player.joinedRound && player.joinedRound > 1
      ? `<span class="late-joiner-tag">${t('standings.joinedInRound', 'Joined R{{round}}').replace('{{round}}', String(player.joinedRound))}</span>`
      : '';
    html += `
      <tr>
        <td><strong>${index + 1}</strong></td>
        <td>
          ${playerDisplay}${lateJoinerTag}
          ${player.rating ? `<br><small>(${player.rating})</small>` : ''}
        </td>
        <td><strong>0</strong></td>
        <td>0-0-0</td>
        ${emptyTiebreakCells}
      </tr>
    `;
  });

  html += `
        </tbody>
      </table>
    </div>
  `;

  return html;
}

/**
 * Filter standings by category
 * Reloads standings with the selected category filter
 * @param tournamentId - Tournament ID
 * @param categoryId - Category ID to filter by (empty string for all)
 */
async function filterStandingsByCategory(tournamentId: string, categoryId: string): Promise<void> {
  const API_BASE_URL = (window as any).API_BASE_URL || '/api/tournaments';

  try {
    // Get current round from the active button
    const activeButton = document.querySelector(`.standings-round-btn.btn-primary[data-tournament="${tournamentId}"]`) as HTMLButtonElement;
    const round = activeButton?.dataset.round || '';

    // Build URL with category filter
    let url = `${API_BASE_URL}/${tournamentId}/standings`;
    const params = new URLSearchParams();
    if (round && round !== '0') {
      params.append('round', round);
    }
    if (categoryId) {
      params.append('categoryId', categoryId);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, { credentials: 'include' });
    const data = await response.json();

    // Fetch categories for re-rendering
    const categoriesResponse = await fetch(`${API_BASE_URL}/${tournamentId}/categories`, { credentials: 'include' });
    const categories = categoriesResponse.ok ? await categoriesResponse.json() : [];

    // Re-render standings with filtered data
    const standingsContainer = document.getElementById(`standings-${tournamentId}`);

    if (standingsContainer && data.standings) {
      standingsContainer.innerHTML = renderStandings(
        data.standings,
        data.tournament,
        tournamentId,
        data.round || null,
        categories,
        categoryId || null
      );
    }
  } catch (error) {
    console.error('Error filtering standings by category:', error);
  }
}

// Export to window for global access
window.renderStandings = renderStandings;
window.renderRankingInStandings = renderRankingInStandings;
window.filterStandingsByCategory = filterStandingsByCategory;
window.switchStandingsView = switchStandingsView;
